<template>
  <Component
    :is="type"
    class="streem-modal2-mask"
    :class="{
      isMobile: $isMobile,
      'fill-height': fullHeight,
      'menu-offset': offsetMenu && $isDesktop,
    }"
    form-class="full-height full-width"
    @click="clickOutside"
    @submit="$emit('submit', $event)"
  >
    <div
      class="stream-modal2-container"
      :class="modalClass"
      :style="modalWidth"
    >
      <div
        class="streem-modal2 column no-wrap"
        :style="modalHeight"
        @click.stop
      >
        <div class="relative">
          <BaseButton
            v-if="!hideCloseButton"
            :aria-label="$t('global.close')"
            circle
            class="streem-modal2-close"
            :class="{
              'streem-modal2-close__with-header': hasHeader,
            }"
            :color="closeButtonColor"
            flat
            icon="close"
            new-design
            new-icon
            @click="tryClose"
          />

          <div
            v-if="hasHeader"
            class="streem-modal2-header"
            :class="headerClass"
          >
            <slot name="header">
              <div class="streem-modal2-header-title">
                {{ title }}
              </div>
            </slot>
          </div>
        </div>

        <LoadingBar
          v-if="loading"
          height="2"
        />

        <div
          class="streem-modal2-content"
          :class="contentClass"
          :style="contentStyle"
        >
          <slot />
        </div>
        <div
          v-if="hasButtonsSlot"
          class="streem-modal2-buttons"
          :class="buttonsClass"
        >
          <slot
            :close="tryClose"
            name="buttons"
          />
        </div>
      </div>
    </div>
  </Component>
</template>

<script>
import { getCurrentInstance } from "vue";

import { BaseButton, FormWrapper, LoadingBar } from "shared/components/base";
import useModal from "shared/composables/useModal";

export default {
  name: "Modal",
  components: {
    BaseButton,
    LoadingBar,
    FormWrapper,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    modalClass: {
      type: [Array, Object, String],
      default: "",
    },
    modalStyle: {
      type: Object,
      default: () => ({}),
    },
    contentClass: {
      type: [Array, Object, String],
      default: "q-pa-md",
    },
    contentStyle: {
      type: Object,
      default: () => ({}),
    },
    headerClass: {
      type: [Array, Object, String],
      default: "q-pa-md q-pr-xl",
    },
    buttonsClass: {
      type: [Array, Object, String],
      default: "streem-modal2-buttons__layout",
    },
    closeButtonColor: {
      type: String,
      default: "",
    },
    hideCloseButton: {
      type: Boolean,
    },
    persistent: {
      type: Boolean,
    },
    fullHeight: {
      type: Boolean,
    },
    offsetMenu: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    form: {
      type: Boolean,
    },
  },
  emits: ["before-close", "close", "submit"],
  setup() {
    const { parent } = getCurrentInstance();
    const modalName = parent.type.name;
    const { close } = useModal(modalName);

    return { close };
  },
  computed: {
    type() {
      return this.form ? "FormWrapper" : "div";
    },
    hasButtonsSlot() {
      return Boolean(this.$slots.buttons);
    },
    hasHeader() {
      return Boolean(this.$slots.header || this.title);
    },
    modalWidth() {
      if (this.modalStyle.maxWidth) {
        return { maxWidth: this.modalStyle.maxWidth };
      }

      if (this.modalStyle.width) {
        return { width: this.modalStyle.width };
      }

      return {};
    },
    modalHeight() {
      if (this.modalStyle.height) {
        return { height: this.modalStyle.height };
      }

      return {};
    },
  },
  methods: {
    clickOutside() {
      if (!this.persistent) {
        this.tryClose();
      }
    },
    tryClose() {
      let hasCanceled = false;

      const cancel = () => {
        hasCanceled = true;
      };

      this.$emit("before-close", cancel);
      if (hasCanceled) return;

      this.$emit("close");
      this.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .streem-modal2,
.modal-leave-to .streem-modal2 {
  transform: scale(1.1);
}

.streem-modal2-mask {
  position: fixed;
  inset: 0;
  background-color: rgba(0 0 0 / 50%);
  transition: opacity 0.3s ease;
  z-index: 4000;
  overflow: hidden auto;
  padding: $modal-gutter;

  &.menu-offset {
    padding-left: calc(#{$streem-menu-side-bar-width} + #{$modal-gutter});
  }

  .stream-modal2-container {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 500px;
    min-height: calc(100% - #{$modal-gutter} * 2);
    position: relative;
    margin: 0 auto;
  }

  .streem-modal2 {
    position: relative;
    width: 100%;
    border-radius: 5px;
    background-color: white;
    box-shadow: 0 3px 10px 0 rgba(23 23 23 / 30%);
    z-index: 7500;
    transition: all 0.3s ease;

    &-close {
      position: absolute;
      z-index: 1;
      right: 8px;
      top: 8px;

      &__with-header {
        top: 50%;
        margin-top: -16px;
      }
    }

    &-header {
      border-bottom: 1px solid $hover-background;

      &-title {
        font-size: 16px;
        font-weight: bold;
        line-height: normal;
      }
    }

    &-buttons {
      border-top: 1px solid $hover-background;

      &__layout {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding: var(--s-spacing-md);
        gap: var(--s-spacing-sm);
      }
    }
  }

  &.fill-height {
    overflow: hidden;

    .stream-modal2-container {
      min-height: auto;
      height: 100%;
      display: block;
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);

      .streem-modal2 {
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        margin-top: 0;

        .streem-modal2-content {
          flex: 1;
          overflow: auto;
        }
      }
    }
  }

  &.isMobile {
    .stream-modal2-container {
      width: 100% !important;
    }
  }
}
</style>
